import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Edit, Presentation, Plus, Phone, Mail, MapPin, FileText } from "lucide-react";
import { CustomerMeasurementUpload } from "@/components/admin/customers/CustomerMeasurementUpload";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: customer, isLoading: customerLoading, refetch: refetchCustomer } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: estimates, isLoading: estimatesLoading } = useQuery({
    queryKey: ["customer-estimates", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*")
        .eq("customer_id", id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const handleStartSalesPresentation = async () => {
    if (!customer) return;

    try {
      // Get active price book for customer's county
      const { data: priceBook } = await supabase
        .from("price_books")
        .select("id")
        .eq("county", customer.county || "Suffolk")
        .eq("is_active", true)
        .order("effective_date", { ascending: false })
        .limit(1)
        .single();

      if (!priceBook) {
        toast({
          title: "No active price book",
          description: `No active price book found for ${customer.county || "Suffolk"} county`,
          variant: "destructive",
        });
        return;
      }

      // Create new estimate
      const estimateNumber = `EST-${Date.now()}`;
      const { data: newEstimate, error: estimateError } = await supabase
        .from("estimates")
        .insert({
          customer_id: customer.id,
          estimate_number: estimateNumber,
          customer_name: customer.name,
          customer_email: customer.email,
          customer_phone: customer.phone,
          customer_address: customer.address,
          city: customer.city,
          county: customer.county,
          zip_code: customer.zip_code,
          price_book_id: priceBook.id,
          status: "draft",
        })
        .select()
        .single();

      if (estimateError) throw estimateError;

      // If customer has measurements, create measurement record
      if (customer.measurement_data && typeof customer.measurement_data === 'object') {
        const data = customer.measurement_data as Record<string, any>;
        // Handle nested structure from edge functions: { measurements: {...}, diagnostics: {...} }
        const measurements = data.measurements || data;
        
        const { error: measurementError } = await supabase
          .from("measurements")
          .insert({
            estimate_id: newEstimate.id,
            customer_id: customer.id,
            area_sqft: measurements.area_sqft || null,
            pitch: measurements.pitch || null,
            layers: measurements.layers || 1,
            valley_lf: measurements.valley_lf || null,
            ridge_lf: measurements.ridge_lf || null,
            rake_lf: measurements.rake_lf || null,
            eave_lf: measurements.eave_lf || null,
            hip_lf: measurements.hip_lf || null,
            flashing_lf: measurements.flashing_lf || null,
            wall_step_lf: measurements.stepflashing_lf || null,
            drip_edge_lf: measurements.rake_lf && measurements.eave_lf 
              ? (measurements.rake_lf + measurements.eave_lf) 
              : null,
            skylight_count: measurements.skylight_count || 0,
            chimney_count: measurements.chimney_count || 0,
            vent_count: measurements.vent_count || 0,
            pipe_count: measurements.pipe_count || 0,
            fan_count: measurements.fan_count || 0,
            source: customer.measurement_source || "manual",
            notes: customer.measurement_notes,
            measurement_type: "area",
            waste_factor: 1.10,
          });

        if (measurementError) {
          console.error("Failed to copy measurements:", measurementError);
          toast({
            title: "Warning",
            description: "Measurements copied but some fields may be missing",
            variant: "default",
          });
        }
      }

      toast({ title: "Estimate created! Starting sales presentation..." });
      navigate(`/admin/estimates/presentation/${newEstimate.id}`);
    } catch (error: any) {
      console.error("Failed to create estimate:", error);
      toast({
        title: "Failed to create estimate",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (customerLoading) {
    return <div className="text-center py-8">Loading customer...</div>;
  }

  if (!customer) {
    return <div className="text-center py-8">Customer not found</div>;
  }

  return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin/customers")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{customer.name}</h1>
              <p className="text-muted-foreground mt-1">Customer Profile</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/customers/${customer.id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button onClick={handleStartSalesPresentation}>
              <Presentation className="h-4 w-4 mr-2" />
              Start Sales Presentation
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Phone</div>
                  <div className="font-medium">{customer.phone}</div>
                </div>
              </div>
              {customer.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm text-muted-foreground">Email</div>
                    <div className="font-medium">{customer.email}</div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <div className="text-sm text-muted-foreground">Address</div>
                  <div className="font-medium">
                    {customer.address && <div>{customer.address}</div>}
                    {customer.city && customer.county && (
                      <div>
                        {customer.city}, {customer.county} {customer.zip_code}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {customer.notes && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Notes</div>
                  <div className="text-sm">{customer.notes}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <CustomerMeasurementUpload
            customerId={customer.id}
            currentFileUrl={customer.measurement_file_url}
            currentData={customer.measurement_data}
            currentSource={customer.measurement_source}
            uploadedAt={customer.measurement_uploaded_at}
            notes={customer.measurement_notes}
            measurementPdfUrl={customer.measurement_pdf_url}
            projectPhotos={Array.isArray(customer.project_photos) ? customer.project_photos : []}
            onUploadSuccess={refetchCustomer}
          />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Estimates</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/estimates?customerId=${customer.id}`)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Estimate
            </Button>
          </CardHeader>
          <CardContent>
            {estimatesLoading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading estimates...
              </div>
            ) : !estimates || estimates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No estimates yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Estimate #</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {estimates.map((estimate) => (
                    <TableRow key={estimate.id}>
                      <TableCell className="font-medium">
                        {estimate.estimate_number}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            estimate.status === "approved"
                              ? "default"
                              : estimate.status === "sent"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {estimate.status}
                        </Badge>
                      </TableCell>
                      <TableCell>${estimate.total.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(estimate.created_at), "PP")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/estimates/${estimate.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
  );
}
